import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import {
  clinicFeaturesApi,
  clinicFeaturesQueryKeys,
  type ClinicFeatures,
} from "../../shared/api/clinicFeatures";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ToggleRow } from "./components/ToggleRow";

/**
 * Cài đặt → Phòng khám → Kê toa & Thuốc (ADR-0027).
 *
 * Two operating models on one switchboard: clinical-only prescribing (drug name /
 * dose / frequency / instructions) vs pharmacy-linked prescribing (quantity, unit
 * price, total, payment, stock). Child toggles collapse when their parent is off,
 * mirroring the server-side normalisation.
 */
export function PrescribingSettingsPage() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: clinicFeaturesQueryKeys.current(),
    queryFn: () => clinicFeaturesApi.get(),
  });

  const [form, setForm] = useState<ClinicFeatures | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState(0);

  useEffect(() => {
    if (query.data) setForm(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (next: ClinicFeatures) => clinicFeaturesApi.update(next),
    onSuccess: (data) => {
      qc.setQueryData(clinicFeaturesQueryKeys.current(), data);
      setForm(data);
      setError(null);
      setSavedAt(Date.now());
    },
    onError: (e) => setError(apiError(e).detail),
  });

  if (!form) {
    return (
      <div>
        <PageHeader title="Kê toa & Thuốc" />
        <p className="text-sm text-muted-foreground">Đang tải…</p>
      </div>
    );
  }

  // Effective (collapsed) values — a child is only "on" if its parent chain is on.
  const prescribe = form.prescribeEnabled;
  const manageInv = prescribe && form.manageInventory;
  const selling = manageInv && form.allowSelling;
  const showPrice = selling && form.showPriceOnRx;
  const deduct = manageInv && form.deductStockOnDispense;

  const set = (patch: Partial<ClinicFeatures>) => setForm({ ...form, ...patch });

  return (
    <div>
      <PageHeader
        title="Kê toa & Thuốc"
        description="Cấu hình kê toa theo mô hình hoạt động thực tế: chỉ kê toa lâm sàng, hay có quản lý và bán thuốc gắn với kho."
      />

      <Card className="max-w-2xl">
        <CardContent className="divide-y p-5">
          <ToggleRow
            label="Cho phép kê toa"
            description="Bác sĩ ghi nhận tên thuốc, cách dùng, liều dùng, số lần dùng và hướng dẫn."
            checked={prescribe}
            onChange={(v) => set({ prescribeEnabled: v })}
          />
          <ToggleRow
            label="Quản lý kho thuốc"
            description="Theo dõi tồn kho thuốc của cơ sở."
            checked={manageInv}
            disabled={!prescribe}
            onChange={(v) => set({ manageInventory: v })}
          />
          <ToggleRow
            label="Cho phép bán thuốc"
            description="Toa thuốc gắn với số lượng xuất, đơn giá, thành tiền và thanh toán."
            checked={selling}
            disabled={!manageInv}
            onChange={(v) => set({ allowSelling: v })}
          />
          <ToggleRow
            label="Hiển thị giá thuốc trên toa"
            description="In đơn giá và thành tiền trên toa thuốc."
            checked={showPrice}
            disabled={!selling}
            onChange={(v) => set({ showPriceOnRx: v })}
          />
          <ToggleRow
            label="Trừ tồn kho khi cấp thuốc"
            description="Tự động trừ số lượng khỏi kho khi cấp/bán thuốc theo toa."
            checked={deduct}
            disabled={!manageInv}
            onChange={(v) => set({ deductStockOnDispense: v })}
          />
        </CardContent>
      </Card>

      {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

      <div className="mt-4 flex items-center gap-3">
        <Button type="button" disabled={save.isPending} onClick={() => save.mutate(form)}>
          {save.isPending ? "Đang lưu…" : "Lưu"}
        </Button>
        {savedAt > 0 && !save.isPending && (
          <span className="inline-flex items-center gap-1 text-sm text-emerald-600">
            <Check size={16} /> Đã lưu
          </span>
        )}
      </div>
    </div>
  );
}
